import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Eye, MousePointerClick, BarChart3, RefreshCw, ExternalLink,
  AlertTriangle, Target, FileText, Calendar, User as UserIcon,
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { marketingBlogAPI } from '../../api/api';
import KpiCard, { fmtNum, fmtPct, fmtPos, fmtFecha } from '../../components/marketing/KpiCard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const RANGOS = [
  { v: 30, label: '30 días' },
  { v: 90, label: '90 días' },
  { v: 180, label: '6 meses' },
];

export default function MarketingBlogPostDetalle() {
  const { slug } = useParams();
  const [dias, setDias] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const cargar = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketingBlogAPI.post(slug, dias);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando detalle');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [slug, dias]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await marketingBlogAPI.postSyncGsc(slug, dias);
      alert(`✅ GSC sincronizado: ${res.data.dias_sincronizados} días · ${res.data.filas_gsc} filas crudas`);
      await cargar();
    } catch (e) {
      alert('Error sincronizando GSC: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Cargando detalle…</div>;
  if (error) return (
    <div className="p-6">
      <Link to="/marketing/blog" className="inline-flex items-center text-sm text-duetto-primary hover:underline mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Volver al inventario
      </Link>
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
    </div>
  );
  if (!data) return null;

  const { post, totales, serie, top_queries, oportunidades, rango } = data;

  const chart = {
    labels: serie.map((d) => d.fecha),
    datasets: [
      {
        label: 'Impresiones',
        data: serie.map((d) => d.impressions || 0),
        borderColor: '#1A9169',
        backgroundColor: 'rgba(26,145,105,0.10)',
        yAxisID: 'y',
        fill: true,
        tension: 0.3,
      },
      {
        label: 'Clicks',
        data: serie.map((d) => d.clicks || 0),
        borderColor: '#67CEA9',
        backgroundColor: 'rgba(103,206,169,0.10)',
        yAxisID: 'y',
        tension: 0.3,
      },
      {
        label: 'Posición media',
        data: serie.map((d) => d.position || null),
        borderColor: '#f59e0b',
        backgroundColor: 'transparent',
        yAxisID: 'y1',
        borderDash: [4, 4],
        tension: 0.3,
        spanGaps: true,
      },
    ],
  };
  const chartOpts = {
    responsive: true,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'bottom' } },
    scales: {
      y: { type: 'linear', position: 'left', beginAtZero: true, title: { display: true, text: 'Impresiones / Clicks' } },
      y1: {
        type: 'linear', position: 'right', reverse: true, suggestedMin: 1,
        grid: { drawOnChartArea: false }, title: { display: true, text: 'Posición media (1 = mejor)' },
      },
    },
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5">
      {/* Breadcrumb + acciones */}
      <div className="flex items-center justify-between">
        <Link to="/marketing/blog" className="inline-flex items-center text-sm text-duetto-primary hover:underline">
          <ArrowLeft className="w-4 h-4 mr-1" /> Volver al inventario
        </Link>
        <div className="flex items-center gap-2">
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-duetto-primary"
          >
            <ExternalLink className="w-4 h-4" />
            Ver en la web
          </a>
          <button
            onClick={sync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-duetto-primary text-white text-sm rounded-md hover:bg-opacity-90 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando…' : 'Sync GSC granular'}
          </button>
        </div>
      </div>

      {/* Header del post */}
      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-start gap-4">
          {post.hero_image && (
            <img
              src={post.hero_image.startsWith('http') ? post.hero_image : `https://duettocatering.com${post.hero_image}`}
              alt={post.hero_image_alt || post.title}
              className="w-28 h-28 object-cover rounded-lg flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-gray-900 mb-1">{post.title}</h1>
            {post.description && <p className="text-sm text-gray-600 mb-2">{post.description}</p>}
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtFecha(post.pub_date)}</span>
              {post.updated_date && (
                <span className="inline-flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> actualizado {fmtFecha(post.updated_date)}
                </span>
              )}
              {post.author && <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {post.author}</span>}
              {post.word_count && <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {post.word_count} palabras</span>}
              {(post.categories || []).map((c) => (
                <span key={c} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{c}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Selector de rango */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {RANGOS.map((r) => (
            <button
              key={r.v}
              onClick={() => setDias(r.v)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                dias === r.v ? 'bg-white text-duetto-primary shadow-sm font-semibold' : 'text-gray-600 hover:text-gray-900'
              }`}
            >{r.label}</button>
          ))}
        </div>
        <span className="text-xs text-gray-500">{rango.desde} → {rango.hasta}</span>
      </div>

      {/* KPIs del rango */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Impresiones" value={fmtNum(totales.impressions)} icon={Eye} />
        <KpiCard label="Clicks" value={fmtNum(totales.clicks)} icon={MousePointerClick} />
        <KpiCard label="CTR" value={fmtPct(totales.ctr)} icon={BarChart3} />
        <KpiCard label="Posición media" value={fmtPos(totales.position)} icon={BarChart3} hint={`${totales.dias_con_datos} días con datos`} />
      </div>

      {/* Gráfica temporal */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">Evolución temporal (GSC)</h3>
        {serie.length === 0 ? (
          <div className="text-sm text-gray-500 text-center py-12 space-y-2">
            <p>Sin datos GSC granulares aún para este post.</p>
            <p className="text-xs">Pulsa <strong>Sync GSC granular</strong> arriba para tirar los últimos {dias} días.</p>
          </div>
        ) : (
          <Line data={chart} options={chartOpts} />
        )}
      </div>

      {/* Top queries + Oportunidades */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PanelQueries
          titulo="Top queries (GSC)"
          icon={Target}
          queries={top_queries}
          emptyMsg="Sin queries registradas en GSC para este post en el rango. Lanza el sync granular si no lo has hecho."
        />
        <PanelOportunidades
          queries={oportunidades}
          emptyMsg="Sin oportunidades detectadas (no hay queries con impresiones ≥5 en posición 4-15)."
        />
      </div>
    </div>
  );
}

function PanelQueries({ titulo, icon: Icon, queries, emptyMsg }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-duetto-primary" />}
        <h3 className="font-semibold text-gray-900">{titulo}</h3>
      </div>
      {!queries || queries.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">{emptyMsg}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 px-2 font-medium">Query</th>
                <th className="text-right py-2 px-2 font-medium">Impr.</th>
                <th className="text-right py-2 px-2 font-medium">Clicks</th>
                <th className="text-right py-2 px-2 font-medium">CTR</th>
                <th className="text-right py-2 px-2 font-medium">Pos.</th>
              </tr>
            </thead>
            <tbody>
              {queries.map((q) => (
                <tr key={q.query} className="border-b last:border-0">
                  <td className="py-2 px-2 text-gray-900">{q.query}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtNum(q.impressions)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtNum(q.clicks)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtPct(q.ctr)}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtPos(q.position)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PanelOportunidades({ queries, emptyMsg }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <h3 className="font-semibold text-gray-900">Oportunidades</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Queries en posición 4-15 con ≥5 impresiones. Empujar contenido o
        enlaces internos hacia ellas puede llevarlas al top 3.
      </p>
      {!queries || queries.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">{emptyMsg}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b">
              <tr>
                <th className="text-left py-2 px-2 font-medium">Query</th>
                <th className="text-right py-2 px-2 font-medium">Impr.</th>
                <th className="text-right py-2 px-2 font-medium">Pos. actual</th>
                <th className="text-right py-2 px-2 font-medium">Distancia al top 3</th>
              </tr>
            </thead>
            <tbody>
              {queries.map((q) => (
                <tr key={q.query} className="border-b last:border-0 hover:bg-amber-50">
                  <td className="py-2 px-2 text-gray-900">{q.query}</td>
                  <td className="py-2 px-2 text-right tabular-nums">{fmtNum(q.impressions)}</td>
                  <td className="py-2 px-2 text-right tabular-nums font-semibold text-amber-700">{fmtPos(q.position)}</td>
                  <td className="py-2 px-2 text-right tabular-nums text-xs text-gray-500">
                    −{(q.position - 3).toFixed(1)} pos.
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
